import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Text, Flex } from "@radix-ui/themes";
import { useTheme } from "../../context/ThemeContext";
import "./ChartColours.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const getCSSVariable = (variable) => getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

export const ReviewsByRatingChart = ({ data }) => {
  const { appearance } = useTheme();
  const [fontColor, setFontColor] = useState("");
  const [gridColour, setGridColour] = useState("");
  const [barColour, setBarColour] = useState("");
  const [barBorderColour, setBarBorderColour] = useState("");

  useEffect(() => {
    const fontColourLight = getCSSVariable("--chart-font-colour-light");
    const fontColourDark = getCSSVariable("--chart-font-colour-dark");
    const gridColourLight = getCSSVariable("--chart-grid-colour-light");
    const gridColourDark = getCSSVariable("--chart-grid-colour-dark");
    // Use amber/yellow color for ratings
    const amberColour = getCSSVariable("#4ccce6") || "#4ccce6";
    const amberBorderColour = getCSSVariable("#4ccce6") || "#4ccce6";

    if (appearance === "dark") {
      setFontColor(fontColourDark);
      setGridColour(gridColourDark);
    } else {
      setFontColor(fontColourLight);
      setGridColour(gridColourLight);
    }
    setBarColour(amberColour);
    setBarBorderColour(amberBorderColour);
  }, [appearance]);

  const chartData = data || [
    { rating: '1', count: 0 },
    { rating: '2', count: 0 },
    { rating: '3', count: 0 },
    { rating: '4', count: 0 },
    { rating: '5', count: 0 },
  ];

  // Check if there's any data
  const hasData = chartData.some(item => item.count > 0);

  if (!hasData) {
    return (
      <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}>
        <Text size="2" color="gray">No rating data available</Text>
      </Flex>
    );
  }

  const chartJsData = {
    labels: chartData.map(item => `${item.rating} Star${item.rating !== '1' ? 's' : ''}`),
    datasets: [
      {
        label: 'Reviews',
        data: chartData.map(item => item.count),
        backgroundColor: barColour,
        borderColor: barBorderColour,
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: 'bottom',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function (tooltipItems) {
            return tooltipItems[0].label;
          },
          label: function (context) {
            return `Reviews: ${context.parsed.y}`;
          },
        },
        bodyFont: {
          family: 'Figtree',
        },
        titleFont: {
          family: 'Figtree',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: fontColor,
          font: {
            family: 'Figtree',
            size: 12,
          },
          padding: 8,
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      },
      y: {
        beginAtZero: true,
        position: 'right',
        ticks: {
          color: fontColor,
          font: {
            family: 'Figtree',
            size: 12,
          },
          padding: 8,
          stepSize: 1,
          callback: function (value) {
            if (Number.isInteger(value)) {
              return value;
            }
            return null;
          },
        },
        grid: {
          display: true,
          color: gridColour,
          borderWidth: 1,
          drawTicks: false,
        },
        border: {
          dash: [2, 2],
        },
      },
    },
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Bar data={chartJsData} options={options} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
